import { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  AppLanguage,
  detectLanguageByRegion,
  languageToHtmlLang,
  languageToLocale,
} from "@/lib/language";
import { applyRuntimeTranslations } from "@/lib/runtimeI18n";

type LanguageContextValue = {
  language: AppLanguage;
  locale: string;
  htmlLang: string;
  isDetecting: boolean;
  setLanguage: (language: AppLanguage) => void;
};

const STORAGE_KEY = "qlivar_language";

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const getInitialLanguage = (): AppLanguage => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === "pt" || saved === "en") return saved;
  return "en";
};

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(getInitialLanguage);
  const [isDetecting, setIsDetecting] = useState(true);

  const setLanguage = (nextLanguage: AppLanguage) => {
    setLanguageState(nextLanguage);
    localStorage.setItem(STORAGE_KEY, nextLanguage);
  };

  useEffect(() => {
    let mounted = true;

    const runDetection = async () => {
      const detected = await detectLanguageByRegion();
      if (!mounted) return;
      setLanguageState(detected.language);
      localStorage.setItem(STORAGE_KEY, detected.language);
      setIsDetecting(false);
    };

    runDetection();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = languageToHtmlLang(language);
  }, [language]);

  useEffect(() => {
    if (!document.body) return;

    applyRuntimeTranslations(language, document.body);

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData" && mutation.target instanceof Text) {
          applyRuntimeTranslations(language, mutation.target.parentNode || document.body);
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            applyRuntimeTranslations(language, node);
          }
        });
      });
    });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [language]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      language,
      locale: languageToLocale(language),
      htmlLang: languageToHtmlLang(language),
      isDetecting,
      setLanguage,
    }),
    [isDetecting, language]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}
